import {
  BankApplication,
  BankApplicationStatus,
  Query,
  SupplementalDocumentRequest,
  TokenInfoResultTypenames,
  Typenames,
} from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import { SourceName } from 'features/application/hooks/useApplicationTrackErrorEvents';
import DebtSaleSummarySection from 'features/reactivation/components/DebtSaleSummarySection/DebtSaleSummarySection';
import useDebtSaleDetails from 'features/reactivation/hooks/useDebtSaleDetails';
import useReactivateAccount from 'features/reactivation/hooks/useReactivateAccount';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import GetCustomerApplicationStatus from 'services/graphql/queries/GetCustomerApplicationStatus';
import GetReactivationDocumentRequests from 'services/graphql/queries/GetReactivationDocumentRequests';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import theme from 'shared/styles/theme';
import { useQuery } from 'urql';

interface ReactivationContentSectionProps {
  reactivationDocumentRequestsData: {
    requests?: Array<SupplementalDocumentRequest | null>;
  };
  applicationStatus: BankApplicationStatus | null | undefined;
  reactivationDataFetching: boolean;
  isReactivationFlow: string;
  translatedText: (key: string) => string;
}

function ReactivationContentSection({
  reactivationDocumentRequestsData,
  applicationStatus,
  reactivationDataFetching,
  isReactivationFlow,
  translatedText,
}: ReactivationContentSectionProps) {
  const router = useRouter();
  // if there is a document request, show the verify screen tile for IDV
  if (reactivationDocumentRequestsData?.requests?.length) {
    return (
      <View>
        <View style={styles.textContainer}>
          <View style={styles.title}>
            <TitleLgText>{translatedText('verifyTitle')}</TitleLgText>
          </View>
          <BodyBaseText>{translatedText('verifyDescription')}</BodyBaseText>
          <Button
            label={translatedText('buttons.verify')}
            onPress={() => {
              router.push({
                pathname: '/signup/application-decision',
                params: {
                  sourceName: SourceName.REACTIVATION,
                },
              });
            }}
            isLoading={reactivationDataFetching}
            size="LARGE"
            type="PRIMARY"
            width="FULL"
          />
        </View>
        <View style={styles.sectionSeparator} />
      </View>
    );
  }

  // if the application is in review, show the review tile
  if (
    applicationStatus === BankApplicationStatus.InReview ||
    applicationStatus === BankApplicationStatus.Pending
  ) {
    return (
      <View>
        <View style={styles.textContainer}>
          <View style={styles.title}>
            <TitleLgText>{translatedText('reviewTitle')}</TitleLgText>
          </View>
          <BodyBaseText>{translatedText('reviewDescription')}</BodyBaseText>
        </View>
        <View style={styles.sectionSeparator} />
      </View>
    );
  }

  // all other cases, show the default reactivation apply-now tile
  return (
    <View>
      <View style={styles.textContainer}>
        <View style={styles.title}>
          <TitleLgText>{translatedText('reactivationTitle')}</TitleLgText>
        </View>
        <BodyBaseText>{translatedText('reactivationDescription')}</BodyBaseText>
        <Button
          label={translatedText('buttons.applyNow')}
          onPress={() => {
            router.push('/reactivation/verify-sms');
          }}
          analyticsId="applyNow"
          customAttributes={{
            reactivation_flow: isReactivationFlow,
            step_name: 'signup_identity',
          }}
          isLoading={reactivationDataFetching}
          size="LARGE"
          type="PRIMARY"
          width="FULL"
        />
      </View>
      <View style={styles.sectionSeparator} />
    </View>
  );
}

const Reactivation = () => {
  const router = useRouter();
  const { handleLogout } = useContext<AuthContextProps>(AuthContext);
  const { data: reactivationData, fetching: reactivationDataFetching } =
    useReactivateAccount();

  const reactivationUserId =
    reactivationData?.getTokenInfo?.__typename ===
    TokenInfoResultTypenames.TokenInfoSuccess
      ? reactivationData.getTokenInfo.reactivationUserId
      : undefined;

  const isReactivationFlow = !!reactivationUserId;

  const [{ data: applicationStatusData, fetching: applicationStatusFetching }] =
    useQuery<Query>({
      query: GetCustomerApplicationStatus,
      requestPolicy: 'network-only',
    });

  const bankApplication = applicationStatusData?.applications.find(
    (key) =>
      key &&
      '__typename' in key &&
      key.__typename === Typenames.BankApplication,
  ) as BankApplication;

  const [{ data: documentRequestsData, fetching: documentRequestsFetching }] =
    useQuery<Query>({
      query: GetReactivationDocumentRequests,
      variables: { externalId: bankApplication?.id },
      requestPolicy: 'network-only',
      pause: !applicationStatusData?.applications.length || !reactivationUserId,
    });

  const {
    result: debtSaleDetailsResult,
    error: debtSaleDetailsError,
    fetching: debtSaleDetailsFetching,
  } = useDebtSaleDetails();

  const { t: translatedText } = useTranslation('reactivation', {
    keyPrefix: 'reactivation',
  });

  const reactivationDocumentRequestsData =
    documentRequestsData?.reactivationDocumentRequests as {
      requests?: Array<SupplementalDocumentRequest | null>;
    };

  useViewDidFocusScreenAnalyticsEvent(
    isReactivationFlow
      ? {
          key: 'closed-account-reactivation',
          customAttributes: {
            reactivation_flow: isReactivationFlow.toString(),
          },
        }
      : undefined,
  );

  const ClosedTitleComponent = reactivationUserId ? TitleMdText : TitleLgText;

  const insets = useSafeAreaInsets();

  const applicationStatus = (
    applicationStatusData?.applications.find(
      (key) =>
        key &&
        '__typename' in key &&
        key.__typename === Typenames.BankApplication,
    ) as BankApplication
  )?.bankApplicationStatus;

  if (
    reactivationDataFetching ||
    applicationStatusFetching ||
    documentRequestsFetching ||
    debtSaleDetailsFetching
  ) {
    return (
      <View style={styles.loadingContainer}>
        <FullScreenLoading />
      </View>
    );
  }

  return (
    <View style={[styles.container, { paddingTop: insets.top }]}>
      <Stack.Screen
        options={{
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ScrollView
        contentContainerStyle={{
          paddingBottom: insets.bottom + theme.spacing.m,
        }}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.buttonContainer}>
          <Button
            label={translatedText('buttons.logOut')}
            onPress={async () => {
              await handleLogout();
            }}
            analyticsId="logoutClosedAccount"
            customAttributes={{
              reactivation_flow: isReactivationFlow.toString(),
            }}
            type="TEXT"
            size="LARGE"
            width="HUG"
          />
        </View>
        <View style={styles.textContainer}>
          <View style={styles.title}>
            <ClosedTitleComponent>
              {translatedText('closedTitle')}
            </ClosedTitleComponent>
          </View>
          <BodyBaseText>{translatedText('closedDescription')}</BodyBaseText>
        </View>
        <View>
          <ListItem
            primaryCopy={translatedText('buttons.viewStatements')}
            analyticsId="accountStatements"
            customAttributes={{
              reactivation_flow: isReactivationFlow.toString(),
            }}
            onPress={() => {
              router.push({
                pathname:
                  '/reactivation/statements-and-forms/account-statements/closed-accounts',
              });
            }}
          />
          <View style={styles.hairLineDivider}>
            <HairLineDivider />
          </View>
          <ListItem
            primaryCopy={translatedText('buttons.taxStatements')}
            analyticsId="taxStatements"
            customAttributes={{
              reactivation_flow: isReactivationFlow.toString(),
            }}
            onPress={() => {
              router.push({
                pathname: '/reactivation/statements-and-forms/tax-statements',
              });
            }}
          />
          <View style={styles.hairLineDivider}>
            <HairLineDivider />
          </View>
          <ListItem
            primaryCopy={translatedText('buttons.editMailAddress')}
            analyticsId="editMailAddress"
            customAttributes={{
              reactivation_flow: isReactivationFlow.toString(),
            }}
            onPress={() => {
              router.push({
                pathname: '/reactivation/personal-details/change-address',
                params: {
                  isLimitedAccess: 'true',
                  analyticsKey: 'editAddress',
                },
              });
            }}
          />
        </View>
        {isReactivationFlow ? (
          <>
            <View style={styles.sectionSeparator} />
            <ReactivationContentSection
              reactivationDocumentRequestsData={
                reactivationDocumentRequestsData
              }
              applicationStatus={applicationStatus}
              reactivationDataFetching={reactivationDataFetching}
              isReactivationFlow={isReactivationFlow.toString()}
              translatedText={translatedText}
            />
          </>
        ) : null}
        {debtSaleDetailsResult || debtSaleDetailsError ? (
          <>
            {!isReactivationFlow ? (
              <View style={styles.sectionSeparator} />
            ) : null}
            <DebtSaleSummarySection
              result={debtSaleDetailsResult}
              error={debtSaleDetailsError}
            />
          </>
        ) : null}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    alignItems: 'flex-end',
    display: 'flex',
    gap: theme.spacing.m,
    paddingRight: theme.spacing.s,
    paddingTop: theme.spacing.s,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    flexDirection: 'column',
    flexGrow: 1,
    zIndex: 1,
  },
  hairLineDivider: { marginHorizontal: theme.spacing.m },
  loadingContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    gap: 4,
    height: '100%',
    padding: theme.spacing.m,
  },
  sectionSeparator: {
    backgroundColor: theme.color.background.tertiary,
    height: theme.spacing.s,
  },
  textContainer: {
    display: 'flex',
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
  title: {
    paddingVertical: theme.spacing.m,
  },
});

export default Reactivation;
